import { useMemo } from 'react';
import { countries, type ICountry } from 'countries-list';

import { objectKeys } from '@/lib/types';

import { GlobeIcon } from '@/components/icons';
import {
  components,
  DropdownBase,
  type OnChangeValue,
  type SingleValueProps,
  type SingleValue,
} from '@/primitives';

export type CountryOption = {
  label: string;
  value: string;
  details: ICountry;
};

const SingleValue = ({
  children,
  ...props
}: SingleValueProps<CountryOption>) => {
  return (
    <components.SingleValue {...props}>
      {props.hasValue ? props.getValue()[0].value : children}
    </components.SingleValue>
  );
};

export interface DropdownCountryProps {
  countryOptionFilter?: (country: ICountry) => boolean;
  onChange?: (value: SingleValue<CountryOption>) => void;
  value?: CountryOption | null;
}

export function DropdownCountry({
  countryOptionFilter,
  onChange,
  value,
}: DropdownCountryProps) {
  const options = useMemo(() => {
    const countryNames = new Intl.DisplayNames([navigator.language], {
      type: 'region',
    });
    const countryCodes = objectKeys(countries);

    const options: Array<CountryOption> = [];
    countryCodes.forEach((countryCode) => {
      countries[countryCode].name;
      if (countryOptionFilter && !countryOptionFilter(countries[countryCode])) {
        return;
      }
      const label = countryNames.of(countryCode);
      if (label) {
        options.push({
          label: `${countryCode} - ${label}`,
          value: countryCode,
          details: countries[countryCode],
        });
      }
    });

    return options;
  }, [countryOptionFilter]);

  const handleChange = (newValue: OnChangeValue<CountryOption, false>) => {
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <DropdownBase
      label={<GlobeIcon sx={{ marginLeft: '-4px' }} />}
      isMulti={false}
      components={{
        SingleValue,
      }}
      isSearchable={false}
      onChange={handleChange}
      options={options}
      value={value}
    />
  );
}
